
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getFamilyById, updateFamilyDetails } from "@/lib/firebase";
import { Family } from "@/types";
import { FamilySettingsForm } from "@/components/family/FamilySettingsForm";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function FamilySettingsPage() {
  const { currentUser } = useAuth();
  const [family, setFamily] = useState<Family | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Redirect if not parent
  useEffect(() => {
    if (currentUser && currentUser.role !== 'parent') {
      navigate('/');
    }
  }, [currentUser, navigate]);
  
  useEffect(() => {
    document.title = "Family Settings | Shekel";
    
    const fetchFamilyData = async () => {
      if (!currentUser?.familyId) return;
      
      setIsLoading(true);
      try {
        const familyData = await getFamilyById(currentUser.familyId);
        setFamily(familyData as Family);
      } catch (error) {
        console.error("Error fetching family data:", error);
        toast({
          title: "Error",
          description: "Failed to load family data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFamilyData();
  }, [currentUser, toast]);
  
  const handleUpdateFamily = async (name: string, description: string, icon: string) => {
    if (!family || !currentUser?.familyId) return;
    
    setIsSubmitting(true);
    try {
      await updateFamilyDetails(
        currentUser.familyId,
        name,
        description,
        icon
      );
      
      // Update local state
      setFamily({
        ...family,
        name,
        description,
        icon,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error("Error updating family:", error);
      toast({
        title: "Error",
        description: "Failed to update family settings. Please try again.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-shekel-600"></div>
      </div>
    );
  }
  
  if (!family) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Family not found</p>
        <Button onClick={() => navigate("/")} className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }
  
  return (
    <div>
      <Button
        variant="ghost"
        onClick={() => navigate("/")}
        className="mb-4 pl-0 flex items-center"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Button>
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Family Settings</h1>
        <p className="text-muted-foreground">
          Manage your family's profile and settings
        </p>
      </div>
      
      <div className="max-w-3xl">
        <FamilySettingsForm
          family={family}
          onUpdate={handleUpdateFamily}
          isLoading={isSubmitting}
        />
      </div>
    </div>
  );
}
